import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';

/**
 * Filter Source Pill Component  
 * Status indicator showing filter source (SubType/Syllabus)
 * SubType shows green when all selected, Syllabus always gray
 */
const FilterSourcePill = ({
    type, // 'subtype' or 'syllabus'
    value, // string or array of values
    active = false,
    onClear,
    interactive = false,
    availableOptions = [],
    onSelectionChange
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [hovering, setHovering] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const label = type === 'subtype' ? 'Sub-Type' : 'Syllabus';

    // Determine display value
    let displayValue = '';
    if (Array.isArray(value) && value.length > 0) {
        displayValue = value.length === 1 ? value[0] : `${value.length} selected`;
    } else if (typeof value === 'string' && value) {
        displayValue = value;
    }

    // Syllabus is always gray, SubType can be green when fully active
    const isFullyActive = type === 'subtype' && active && (
        !interactive ||
        (Array.isArray(value) && value.length === availableOptions.length)
    );

    const pillClasses = `
    inline-flex flex-col items-center gap-0.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all relative border
    ${active
            ? 'bg-green-100 text-green-800 border-green-200'
            : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
        }
    ${interactive ? 'cursor-pointer hover:shadow-sm' : ''}
  `;

    const handlePillClick = () => {
        if (interactive) {
            setIsOpen(!isOpen);
        }
    };

    const handleToggleOption = (option) => {
        if (!onSelectionChange) return;

        const currentValues = Array.isArray(value) ? value : [];
        const newValues = currentValues.includes(option)
            ? currentValues.filter(v => v !== option)
            : [...currentValues, option];

        onSelectionChange(newValues);
    };

    const handleSelectAll = () => {
        if (onSelectionChange) {
            onSelectionChange(availableOptions);
        }
    };

    const handleClearSelection = () => {
        if (onSelectionChange) {
            onSelectionChange([]);
        }
    };

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <div
                className={pillClasses}
                onClick={handlePillClick}
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
            >
                <div className="flex items-center gap-2 w-full">
                    <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
                    {interactive && (
                        <ChevronDown
                            className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                    )}
                    {hovering && active && onClear && interactive && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onClear();
                            }}
                            className="p-0.5 bg-red-500 text-white hover:bg-red-600 rounded-full transition-colors ml-auto shadow-sm"
                            title="Clear filter"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    )}
                </div>
                {displayValue && (
                    <span className="text-xs font-medium opacity-90 w-full text-left">{displayValue}</span>
                )}
            </div>

            {interactive && isOpen && (
                <div className="absolute z-50 mt-2 w-48 sm:w-64 bg-white border-2 border-gray-200 rounded-xl shadow-xl overflow-hidden">
                    <div className="p-3 border-b border-gray-100 flex gap-2">
                        <button
                            type="button"
                            onClick={handleSelectAll}
                            className="flex-1 px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                        >
                            Select All
                        </button>
                        <button
                            type="button"
                            onClick={handleClearSelection}
                            className="flex-1 px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            Clear
                        </button>
                    </div>

                    <div className="max-h-60 overflow-y-auto custom-scrollbar p-2">
                        {availableOptions.length > 0 ? (
                            availableOptions.map((option, idx) => {
                                const isSelected = Array.isArray(value) && value.includes(option);

                                return (
                                    <label
                                        key={idx}
                                        className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors group"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => handleToggleOption(option)}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                                        />
                                        <span className="flex-1 text-sm font-medium text-gray-700 group-hover:text-gray-900">
                                            {option}
                                        </span>
                                    </label>
                                );
                            })
                        ) : (
                            <div className="p-4 text-center text-sm text-gray-500">
                                No options available
                            </div>
                        )}
                    </div>
                </div>
            )}

            <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #3B82F6;
          border-radius: 10px;
        }
      `}</style>
        </div>
    );
};

export default FilterSourcePill;
